import React, { useState, useEffect, useMemo, useImperativeHandle, forwardRef } from 'react';
import ItemSidePanel from './ItemSidePanel';
import StageSidePanel from './StageSidePanel';

interface Item {
  id: string;
  type: string;
  width?: string;
  height?: string;
  number?: string;
  status?: string;
  stages?: Array<{
    stage: string;
    status: string;
    timestamp?: string;
  }>;
}

interface Product {
  id: string;
  type: string;
  name?: string;
  frame?: Item[];
  panel?: Item[];
  panelWindow?: Item[];
  net?: Item[];
  openableType?: Item[];
}

interface ItemStageSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseAll?: () => void;
  product: Product | null;
  onItemSelect?: (item: Item) => void;
  onStageSelect?: (item: Item, stage: any) => void;
  onBackToProducts?: () => void;
  title?: string;
  order?: any;
}

export interface ItemStageSidePanelRef {
  updateProduct: (product: Product) => void;
}

const ItemStageSidePanel = forwardRef<ItemStageSidePanelRef, ItemStageSidePanelProps>(({
  isOpen,
  onClose,
  onCloseAll,
  product,
  onItemSelect,
  onStageSelect,
  onBackToProducts,
  title,
  order,
}, ref) => {
  const [showStages, setShowStages] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  // Keep local copy of product to avoid re-renders from parent updates
  const [localProduct, setLocalProduct] = useState<Product | null>(product);

  // Expose update function via ref
  useImperativeHandle(ref, () => ({
    updateProduct: (updatedProduct: Product) => {
      if (updatedProduct.id === localProduct?.id) {
        setLocalProduct(updatedProduct);
        
        // If stages panel is open and we have a selected item, update it with latest data
        if (showStages && selectedItem) {
          // Find the updated item from the new product data
          const allItems = [
            ...(updatedProduct.frame || []),
            ...(updatedProduct.panel || []),
            ...(updatedProduct.panelWindow || []),
            ...(updatedProduct.net || []),
            ...(updatedProduct.openableType || []),
          ];
          
          const updatedItem = allItems.find(item => item.id === selectedItem.id);
          if (updatedItem) {
            // Update selected item with latest data including stages
            setSelectedItem(updatedItem);
          }
        }
      }
    },
  }), [localProduct?.id, showStages, selectedItem]);

  // Update local product when prop changes (but only if it's a different product or data actually changed)
  useEffect(() => {
    if (!product) {
      setLocalProduct(null);
      return;
    }
    
    if (product.id !== localProduct?.id) {
      // Different product - reset everything
      setLocalProduct(product);
      setShowStages(false);
      setSelectedItem(null);
    } else if (localProduct) {
      // Same product ID - check if data actually changed
      // Only update if stages or items have changed (deep comparison for critical fields)
      const productChanged = JSON.stringify({
        frame: product.frame?.map(i => ({ id: i.id, status: i.status, stages: i.stages })),
        panel: product.panel?.map(i => ({ id: i.id, status: i.status, stages: i.stages })),
        panelWindow: product.panelWindow?.map(i => ({ id: i.id, status: i.status, stages: i.stages })),
        net: product.net?.map(i => ({ id: i.id, status: i.status, stages: i.stages })),
        openableType: product.openableType?.map(i => ({ id: i.id, status: i.status, stages: i.stages })),
      }) !== JSON.stringify({
        frame: localProduct.frame?.map(i => ({ id: i.id, status: i.status, stages: i.stages })),
        panel: localProduct.panel?.map(i => ({ id: i.id, status: i.status, stages: i.stages })),
        panelWindow: localProduct.panelWindow?.map(i => ({ id: i.id, status: i.status, stages: i.stages })),
        net: localProduct.net?.map(i => ({ id: i.id, status: i.status, stages: i.stages })),
        openableType: localProduct.openableType?.map(i => ({ id: i.id, status: i.status, stages: i.stages })),
      });
      
      if (productChanged) {
        // Update silently - don't reset stages or selected item
        setLocalProduct(product);
        
        // If stages panel is open and we have a selected item, update it with latest data
        if (showStages && selectedItem) {
          // Find the updated item from the new product data
          const allItems = [
            ...(product.frame || []),
            ...(product.panel || []),
            ...(product.panelWindow || []),
            ...(product.net || []),
            ...(product.openableType || []),
          ];
          
          const updatedItem = allItems.find(item => item.id === selectedItem.id);
          if (updatedItem) {
            // Update selected item with latest data including stages
            setSelectedItem(updatedItem);
          }
        }
      }
    } else {
      // No local product yet - set it
      setLocalProduct(product);
    }
  }, [product, localProduct?.id, showStages, selectedItem]);

  // Reset local state when panel is closed
  useEffect(() => {
    if (!isOpen) {
      setShowStages(false);
      setSelectedItem(null);
    }
  }, [isOpen]);

  const handleItemSelect = (item: Item) => {
    setSelectedItem(item);
    setShowStages(true);
    onItemSelect?.(item);
  };

  const handleBackToItems = () => {
    setShowStages(false);
    setSelectedItem(null);
  };

  const handleClose = () => {
    setShowStages(false);
    setSelectedItem(null);
    onClose();
  };

  const handleStageSelect = (item: Item, stage: any) => {
    onStageSelect?.(item, stage);
  };

  const handleCloseStagePanel = () => {
    setShowStages(false);
    setSelectedItem(null);
  };

  return (
    <>
      {/* Item Panel - always rendered when isOpen */}
      <ItemSidePanel
        isOpen={isOpen}
        onClose={handleClose}
        onCloseAll={onCloseAll}
        product={localProduct}
        selectedItem={selectedItem}
        onItemSelect={handleItemSelect}
        onBackToItems={handleBackToItems}
        onBackToProducts={onBackToProducts}
        showStages={showStages}
        title={title}
        order={order}
      />

      {/* Stage Panel - rendered when an item is selected */}
      {showStages && selectedItem && (
        <StageSidePanel
          isOpen={showStages}
          onClose={handleCloseStagePanel}
          onCloseAll={onCloseAll}
          item={selectedItem}
          onBackToItems={handleBackToItems}
          title={`${selectedItem.number || selectedItem.id} - Stages`}
        />
      )}
    </>
  );
});

ItemStageSidePanel.displayName = 'ItemStageSidePanel';

export default ItemStageSidePanel;
